<template>
  <div class="col-md-12 questions">
    <div class="form-group row">
      <label class="col-sm-2 col-form-label">Profile Questions</label>

      <div class="col-sm-10">
        <div class="profile-questions__header d-flex flex-wrap align-items-center">
          <span class="profile-questions__add-label me-2">Add a question:</span>
          <button
            v-for="option in typeOptions"
            :key="option.value"
            type="button"
            class="btn btn-sm btn-alt-secondary profile-questions__add-btn me-2 mb-1"
            @click="addQuestion(option.value)"
          >
            <i class="fa fa-plus me-1" />
            {{ option.label }}
          </button>
          <span class="profile-questions__count text-muted small mb-1">
            {{ questionItems.length }} {{ questionItems.length === 1 ? "question" : "questions" }}
          </span>
        </div>
      </div>
    </div>

    <div class="form-group row">
      <div class="col-sm-10 offset-sm-2">
        <div
          v-if="questionItems.length"
          class="profile-questions__list"
        >
          <profile-question-card
            v-for="(question, index) in questionItems"
            :key="question.id"
            :ref="setCardRef"
            class="profile-questions__card"
            :question="question"
            :index="index"
            :total="questionItems.length"
            @input="updateQuestion(index, $event)"
            @remove="removeQuestion(index)"
            @duplicate="duplicateQuestion(index)"
          />
        </div>

        <div
          v-else
          class="profile-questions__empty-state text-center py-5"
        >
          <div class="profile-questions__empty-icon mx-auto mb-3">
            <i class="far fa-question-circle" />
          </div>
          <h4 class="h5 mb-2">
            No profile questions yet
          </h4>
          <p class="text-muted mb-0">
            Use the buttons above to add screening questions experts answer before they are shortlisted.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from "vue";
import ProfileQuestionCard from "./ProfileQuestionCard";
import { DEFAULT_DESCRIPTION_LABEL, DEFAULT_QUESTION } from "./profileQuestionDefaults";

export default {
    name: "ProfileQuestions",
    components: {
        ProfileQuestionCard,
    },
    props: {
        questions: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            questionItems: [],
            cardRefs: [],
            defaultQuestion: DEFAULT_QUESTION,
            typeOptions: [
                { value: "text", label: "Free Text" },
                { value: "single", label: "Single Choice" },
                { value: "multiple", label: "Multiple Choice" },
            ],
        };
    },
    beforeUpdate() {
        this.cardRefs = [];
    },
    mounted() {
        this.questionItems = this.questions.length ? this.questions.map(question => ({
            id: question.id,
            question: question.question,
            type: question.type || "text",
            options: (question.options || []).map(option => ({ id: option.id, text: option.text })),
            descriptionEnabled: question.descriptionEnabled !== false,
            descriptionLabel: question.descriptionLabel || "",
        })) : [];

        this._onProfileQuestionsSet = this.onProfileQuestionsSet.bind(this);
        window.addEventListener("profile-questions:set", this._onProfileQuestionsSet);
    },
    beforeDestroy() {
        window.removeEventListener("profile-questions:set", this._onProfileQuestionsSet);
        this.cardRefs = [];
    },
    methods: {
        setCardRef(el) {
            if (el) {
                this.cardRefs.push(el);
            }
        },
        addQuestion(type = "text") {
            const newQuestion = {
                id: btoa(_.uniqueId("question_")),
                question: this.defaultQuestion,
                type,
                options: type === "text"
                    ? []
                    : [
                        { id: btoa(_.uniqueId("option_")), text: "" },
                        { id: btoa(_.uniqueId("option_")), text: "" },
                    ],
                descriptionEnabled: true,
                descriptionLabel: DEFAULT_DESCRIPTION_LABEL,
            };
            this.questionItems.push(newQuestion);
            this.focusCard(this.questionItems.length - 1);
        },
        duplicateQuestion(index) {
            const source = this.questionItems[index];
            const copy = {
                id: btoa(_.uniqueId("question_")),
                type: source.type,
                question: source.question,
                // Preserve the option text from the source, but with fresh ids.
                options: (source.options || []).map(option => ({
                    id: btoa(_.uniqueId("option_")),
                    text: option.text,
                })),
                descriptionEnabled: source.descriptionEnabled !== false,
                descriptionLabel: source.descriptionLabel || "",
            };
            this.questionItems.splice(index + 1, 0, copy);
        },
        removeQuestion(index) {
            const question = this.questionItems[index];
            const isEdited = question.question !== this.defaultQuestion
                || (question.options || []).some(option => (option.text || "").trim().length > 0);

            if (!isEdited) {
                Vue.delete(this.questionItems, index);
                return;
            }

            this.$bvModal.msgBoxConfirm(
                "Are you sure you want to remove this question?",
                {
                    title: "Remove question",
                    okVariant: "danger",
                    okTitle: "Remove",
                    cancelTitle: "Cancel",
                    centered: true,
                },
            ).then(value => {
                if (value) {
                    Vue.delete(this.questionItems, index);
                }
            }).catch(() => {});
        },
        updateQuestion(index, payload) {
            Vue.set(this.questionItems, index, payload);
        },
        focusCard(index) {
            this.$nextTick(() => {
                const card = this.cardRefs[index];
                if (card && typeof card.focusQuestion === "function") {
                    card.focusQuestion();
                    return;
                }
                // Fallback: focus the textarea by id directly.
                if (card && card.localQuestion) {
                    const el = document.getElementById(`profile-question-${card.localQuestion.id}`);
                    if (el) {
                        el.focus();
                    }
                }
            });
        },
        hasEditedQuestions() {
            return this.questionItems.some(question =>
                question.question !== this.defaultQuestion
                || (question.options || []).some(option => (option.text || "").trim().length > 0),
            );
        },
        async onProfileQuestionsSet(event) {
            const questions = (event.detail && event.detail.questions) || [];

            if (this.hasEditedQuestions()) {
                try {
                    const confirmed = await this.$bvModal.msgBoxConfirm(
                        "This will replace your existing profile questions with the generated ones. Continue?",
                        {
                            title: "Replace profile questions?",
                            okVariant: "primary",
                            okTitle: "Replace",
                            cancelTitle: "Keep mine",
                            centered: true,
                        },
                    );

                    if (!confirmed) {
                        return;
                    }
                } catch (error) {
                    return;
                }
            }

            this.questionItems = questions.map(questionText => ({
                id: btoa(_.uniqueId("question_")),
                question: questionText,
                type: "text",
                options: [],
                descriptionEnabled: true,
                descriptionLabel: DEFAULT_DESCRIPTION_LABEL,
            }));
        },
    },
};
</script>

<style scoped>
.profile-questions__header {
    gap: 0.25rem;
}

.profile-questions__add-label {
    font-weight: 600;
    color: #2f3542;
}

.profile-questions__add-btn {
    cursor: pointer;
}

.profile-questions__add-btn:focus-visible {
    outline: 2px solid #5c80d1;
    outline-offset: 2px;
}

.profile-questions__list {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
}

.profile-questions__empty-state {
    max-width: 34rem;
    margin: 0 auto;
}

.profile-questions__empty-icon {
    width: 4rem;
    height: 4rem;
    border-radius: 999px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f3f6f9;
    color: #5c80d1;
    font-size: 1.25rem;
}

@media (prefers-reduced-motion: reduce) {
    .profile-questions__add-btn {
        transition: none;
    }
}
</style>
